import { useEffect, useState, useRef } from 'react';
import Header from '../Header/Header';
import MapPicker from '../MapPicker/MapPicker';
import { resolveDomestic } from '../../utils/regionState';
import Toast from '../Toast/Toast';
import PlanForm from './PlanForm';
import usePlanList from '../../hooks/usePlanList';
import useLocation from '../../hooks/useLocation';
import { getAddress } from '../../api/addressApi';
import { PlanMainContainer, PlanItem } from './PlanMain.styles';

const EMPTY_FORM = {
    titleTxt: "", startLb: "", startLat: "", startLot: "",
    startHour: "", startMinute: "", destLb: "", destLat: "",
    destLot: "", destHour: "", destMinute: ""
};

const PlanMain = () => {
    const { plans, load, addPlan, updatePlan, removePlan } = usePlanList();
    const { loading: loadingPosition, getLocation } = useLocation();
    const [form, setForm] = useState(EMPTY_FORM);
    const [editIndex, setEditIndex] = useState(null);
    const [showMap, setShowMap] = useState(false);
    const [mapLat, setMapLat] = useState(0);
    const [mapLon, setMapLon] = useState(0);
    const [toastMsg, setToastMsg] = useState(null);
    const selectedPosition = useRef("");

    useEffect(() => {
        window.gtag("event", "Page_일정");
        load();
    }, []);

    const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const buildPlanData = () => ({
        title: form.titleTxt,
        startLb: form.startLb, startLat: form.startLat, startLot: form.startLot,
        startHour: form.startHour, startMinute: form.startMinute,
        destLb: form.destLb, destLat: form.destLat, destLot: form.destLot,
        destHour: form.destHour, destMinute: form.destMinute
    });

    const validate = () => {
        if (form.titleTxt === "") { alert("제목을 입력하세요."); return false; }
        if (form.startLb === "") { alert("출발지를 선택하세요."); return false; }
        if (form.startHour === "" || form.startMinute === "") { alert("출발 시분을 선택하세요."); return false; }
        if (form.destLb === "") { alert("도착지를 선택하세요."); return false; }
        if (form.destHour === "" || form.destMinute === "") { alert("도착 시분을 선택하세요."); return false; }
        return true;
    };

    const add = () => {
        if (!validate()) return;
        addPlan(buildPlanData());
        setForm(EMPTY_FORM);
    };

    const update = () => {
        if (!validate()) return;
        updatePlan(editIndex, buildPlanData());
        setForm(EMPTY_FORM);
        setEditIndex(null);
    };

    const cancelEdit = () => {
        setForm(EMPTY_FORM);
        setEditIndex(null);
    };

    const changeTarget = () => {
        setForm(prev => ({
            ...prev,
            startLb: prev.destLb, startLat: prev.destLat, startLot: prev.destLot,
            startHour: prev.destHour, startMinute: prev.destMinute,
            destLb: prev.startLb, destLat: prev.startLat, destLot: prev.startLot,
            destHour: prev.startHour, destMinute: prev.startMinute
        }));
    };

    const openMap = (type) => {
        selectedPosition.current = type;
        getLocation(
            ({ lat, lon }) => {
                setMapLat(lat);
                setMapLon(lon);
                setShowMap(true);
            },
            () => setToastMsg("위치 정보를 가져올 수 없습니다")
        );
    };

    const handleMapSelect = (lat, lon) => {
        getAddress(lon, lat, (data) => {
            if (selectedPosition.current === "start") {
                setForm(prev => ({ ...prev, startLb: data.address_name, startLat: lat, startLot: lon }));
            } else {
                setForm(prev => ({ ...prev, destLb: data.address_name, destLat: lat, destLot: lon }));
            }
        });
        setShowMap(false);
    };

    const startEdit = (item, idx) => {
        setForm({
            titleTxt: item.title,
            startLb: item.startLb, startLat: item.startLat, startLot: item.startLot,
            startHour: item.startHour, startMinute: item.startMinute,
            destLb: item.destLb, destLat: item.destLat, destLot: item.destLot,
            destHour: item.destHour, destMinute: item.destMinute
        });
        setEditIndex(idx);
    };

    const isEditing = editIndex !== null;

    return (
        <PlanMainContainer>
            <Header />
            <label className='scdFont titleLb'>일정</label><br />

            <PlanForm
                form={form}
                setField={setField}
                onOpenMap={openMap}
                onChangeTarget={changeTarget}
            />

            <div className="btnRow">
                {isEditing ? (
                    <>
                        <button className='addBtn' onClick={update}>일정 수정</button>
                        <button className='cancelBtn' onClick={cancelEdit}>취소</button>
                    </>
                ) : (
                    <button className='addBtn' onClick={add}>일정 추가</button>
                )}
            </div>

            <MapPicker
                show={showMap}
                lat={mapLat}
                lon={mapLon}
                provider={resolveDomestic(mapLat, mapLon) ? 'kakao' : 'google'}
                onSelect={handleMapSelect}
                onClose={() => setShowMap(false)}
            />

            {plans.map((item, i) => (
                <PlanItem key={i} className={editIndex === i ? 'editing' : ''}>
                    <div className="planContent" onClick={() => startEdit(item, i)}>
                        <div className="planTitle">{item.title}</div>
                        <div className="planMeta">
                            {item.startHour}:{item.startMinute} ~ {item.destHour}:{item.destMinute}
                            {' '}{item.startLb.split(" ").slice(-1)[0]} → {item.destLb.split(" ").slice(-1)[0]}
                        </div>
                    </div>
                    <button className="delBtn" onClick={() => { if (editIndex === i) cancelEdit(); removePlan(i); }}>×</button>
                </PlanItem>
            ))}

            {loadingPosition &&
                <div className='loadingMap'>
                    <span>위치 확인 중..</span>
                </div>
            }
            <Toast message={toastMsg} />
        </PlanMainContainer>
    );
};

export default PlanMain;
